import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchPmaDashboardPayload } from "../lib/supabaseTrading";
import {
  emptyDashboardView,
  mapPositionsToPriceChart,
} from "../lib/mapSupabaseToDashboard";
import { isSupabaseConfigured } from "../lib/supabase";

export function usePmaDashboardData() {
  const { isAuthenticated } = useAuth();
  const [raw, setRaw] = useState(null);
  const [view, setView] = useState(() => emptyDashboardView());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [tableDiagnostics, setTableDiagnostics] = useState([]);

  const resetView = useCallback(() => {
    setRaw(null);
    setView(emptyDashboardView());
    setTableDiagnostics([]);
    setLastFetchedAt(null);
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      resetView();
      setLoading(false);
      return { ok: false, error: new Error("Unauthorized") };
    }

    if (!isSupabaseConfigured) {
      setError(new Error("Supabase is not configured"));
      resetView();
      setLoading(false);
      return { ok: false };
    }

    setLoading(true);
    try {
      const result = await fetchPmaDashboardPayload();
      setRaw(result.raw);
      setView(result.view || emptyDashboardView());
      setTableDiagnostics(result.tableDiagnostics || []);
      setError(result.ok ? result.error : result.error);
      if (result.ok) setLastFetchedAt(new Date());
      return result;
    } catch (err) {
      setError(err);
      resetView();
      return { ok: false, error: err };
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, resetView]);

  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      resetView();
      setLoading(false);
    }
  }, [isAuthenticated, refresh, resetView]);

  const hasData = useMemo(() => {
    if (!raw) return false;
    return (
      raw.dailyState ||
      raw.tradingConfig ||
      (raw.decisions?.length ?? 0) > 0 ||
      (raw.positions?.length ?? 0) > 0 ||
      (raw.analysis?.length ?? 0) > 0 ||
      (raw.articles?.length ?? 0) > 0 ||
      (raw.xPosts?.length ?? 0) > 0
    );
  }, [raw]);

  const getPriceChart = useCallback(
    (selectedTicker) => mapPositionsToPriceChart(raw?.positions || [], selectedTicker),
    [raw],
  );

  return {
    raw,
    view,
    loading,
    error,
    lastFetchedAt,
    hasData,
    tableDiagnostics,
    refresh,
    getPriceChart,
    stats: view.stats,
    log: view.log,
    report: view.report,
    pnl: view.pnl,
    distribution: view.distribution,
    articles: view.articles,
    xPosts: view.xPosts,
    positions: view.positions,
    analysis: view.analysis,
    tickers: view.tickers,
    dailyState: view.dailyState,
    tradingConfig: view.tradingConfig,
  };
}
